import React, { useRef, useState } from 'react';
import { Text, Container, Stack, List, Button, Grid } from 'basis';
import { useTheme } from '@emotion/react';
import { LayoutContent, LayoutPage, SaveAndCompleteLaterButton } from 'components';
import { useStoreValue, UPLOAD_DOCUMENTS, REFERRED } from 'store';
import { DocUpload } from 'doc-upload';
import { generateUploadConfig } from 'utils';
import { CHANNEL_INSTORE } from '_config';
import { useDataLayerAndAnalyticsLinks, useErrorTracking } from 'hooks';
import styled from '@emotion/styled';

import { useHistory } from 'react-router-dom/cjs/react-router-dom.min';

export function ProofOfAddress() {
  const methodsRef = useRef(null);
  const [storeState, updateStore] = useStoreValue();
  const theme = useTheme();
  const {
    applicationRef,
    applyAndBuy: { channel },
  } = storeState;
  const { handleErrorTracking } = useErrorTracking();
  const [uploading, setUploading] = useState(false);
  const { trackEventSender } = useDataLayerAndAnalyticsLinks(storeState);
  const history = useHistory();
  const handleSubmit = async () => {
    setUploading(true);
    const result = await methodsRef.current?.startUpload?.();
    if (result) {
      trackEventSender({
        location: 'outcome',
        label: UPLOAD_DOCUMENTS,
      });
    }
    setUploading(false);
  };
  const dmsChannel = channel === CHANNEL_INSTORE ? 'INSTORE' : 'ONLINE';
  const handleDocUploadEvent = evt => {
    switch (evt.type) {
      case 'ERROR':
        setUploading(false);
        handleErrorTracking(evt.error.message || JSON.stringify(evt.error));
        break;
      case 'COMPLETE':
        setUploading(false);

        updateStore({
          activeStep: '/outcome',
          applicationStatus: REFERRED,
        });
        history.push('/outcome');
        break;
      case 'INIT':
        methodsRef.current = {
          startUpload: evt.data.startUpload,
        };
        break;
      default:
        break;
    }
  };
  const config = generateUploadConfig({
    applicationRef,
    channel: dmsChannel,
    documentSubType: 'OTHER',
    documentType: 'PROOF_OF_ADDRESS',
    handleDocUploadEvent,
  });
  return (
    <LayoutPage noProgressStepper>
      <LayoutContent rowsGap="6">
        <Stack gap="8">
          <Text as="h1" textStyle="heading4" color={theme.headingColor}>
            Verify your address
          </Text>
          <Text testId="gotEverythingForNow">
            Application reference number <strong data-hj-suppress>{applicationRef}</strong>
          </Text>
          <Container>
            <Text margin="0 0 4">
              To help complete your Gem Visa application you need to upload one of the following documents.
            </Text>
            <Text margin="0 0 4">
              <strong>
                We need just one document from the following list, containing your name and address, from the last 6
                months.
              </strong>
            </Text>
            <List marginTop="20" fontSize={14} noPadding>
              <List.Item>Utility bill</List.Item>
              <List.Item>Rates notice</List.Item>
              <List.Item>Bank statement or credit card statement</List.Item>
              <List.Item>Loan statement</List.Item>
              <List.Item>Home and contents insurance policy (12 months for annual policy)</List.Item>
              <List.Item>Government issued document</List.Item>
              <List.Item>Rental tenancy agreement</List.Item>
              <List.Item>Property purchase and sale agreements</List.Item>
              <List.Item>Letter from educational institutions</List.Item>
              <List.Item>Kiwisaver statement</List.Item>
            </List>
          </Container>
          <Text>Once you've uploaded your documents our team will review and be in touch within 2 days.</Text>
          <DocUpload {...config} handleDocUploadEvent={handleDocUploadEvent} />
          <Buttons onClick={handleSubmit} loading={uploading} buttonLabel="Submit" />
        </Stack>
      </LayoutContent>
    </LayoutPage>
  );
}

const FormButtonWrapper = styled.div`
  & button {
    padding: 12px 16px;
  }
`;

function Buttons({ onClick, loading, buttonLabel }) {
  return (
    <Grid rowsGap="4" colsGap="10" cols="2" margin="4 0 0 0">
      <Grid.Item colSpan="all" colSpan-sm="0" alignItems="center">
        <FormButtonWrapper>
          <Grid>
            <Button onClick={onClick} loading={loading} padding="0 2">
              {buttonLabel}
            </Button>
          </Grid>
        </FormButtonWrapper>
      </Grid.Item>
      <Grid.Item colSpan="all" colSpan-sm="1" rowSpan-sm="0">
        <FormButtonWrapper>
          <Grid>
            <SaveAndCompleteLaterButton />
          </Grid>
        </FormButtonWrapper>
      </Grid.Item>
    </Grid>
  );
}
